import { DatePeriodEnum } from '@/types/enums'
<template lang="pug">
    v-container(fluid fill-height :class="relativeDateIndicator" align-start)
        v-col(cols="12")
            v-row()
                reservation-toolbar(@periodSelected="onPeriodSelected")

            v-row.pt-4(justify="center")
                ag-grid-vue.ag-theme-balham(
                    domLayout="autoHeight"
                    style="width: 1000px;"
                    :masterDetail="true"
                    :gridOptions="gridOptions"
                    :rowData="rowData"
                    :detailCellRendererParams="detailCellRendererParams"
                    :pinnedBottomRowData="summaryData"
                    @grid-ready="onGridReady"
                )
        v-progress-linear( :active="isLoading" :indeterminate="true" absolute top color="primary accent-4")

</template>

<script lang="ts">
import axios, { CancelTokenSource } from 'axios'
import moment from 'moment'
import Vue from 'vue'
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-enterprise'
import { CURRENT_RESERVATION, LOADING_RESERVATION } from '@/store/getter-types'
import {
  REPORTING_FETCH_RESERVATION_BY_DAY
} from '@/store/action-types'
import DailyReservation from '@/model/reservation/DailyReservation'
import ReservationTableHeader from '@/components/reservation/ReservationTableHeader.vue'
import ReservationTableBody from '@/components/reservation/ReservationTableBody.vue'
import { GridOptions } from 'ag-grid-community'
import * as reservationService from '@/services/reporting/reservations'
import ReservationToolbar from '@/components/reservation/ReservationToolbar.vue'
import { DatePeriod } from '@/model/reservation/DatePeriod'
import { DatePeriodEnum } from '@/types/enums'
import Customer from '@/model/customer/Customer'
import i18n from "@/i18n";

interface ComponentData {
  isLoading: boolean
  rowData: any[]
  gridOptions: GridOptions
  detailCellRendererParams: Object
  summaryData: any[]
  datePeriod: DatePeriod
  customerToDownload: Customer | undefined
  dataCancelSource: CancelTokenSource | undefined
}

export default Vue.extend({
  name: 'DailyReservationPage',

  components: {
    ReservationTableHeader,
    ReservationTableBody,
    AgGridVue,
    ReservationToolbar
  },

  data (): ComponentData {
    return {
      isLoading: false,

      gridOptions: {
        groupUseEntireRow: true,

        defaultColDef: {
          editable: false
        },

        columnDefs: [
          {
            field: 'customerName',
            headerName: i18n.t('reservationPage.customerColumnLabel').toString(),
            rowGroup: true,
            hide: true,
            suppressToolPanel: true,
            openByDefault: true
          },
          { field: 'boarderGroupName', headerName: i18n.t('reservationPage.groupsLabel').toString(), cellRenderer: 'agGroupCellRenderer' },

          {
            headerName: i18n.t('reservationPage.soupLabel').toString(),
            children: [
              { headerName: i18n.t('reservationPage.selectionALabel').toString(), field: 'menuGroup-1.selectionACount' }
            ]
          },
          {
            headerName: i18n.t('reservationPage.mainDishLabel').toString(),
            children: [
              { headerName: i18n.t('reservationPage.selectionALabel').toString(), field: 'menuGroup-2.selectionACount' },
              { headerName: i18n.t('reservationPage.selectionBLabel').toString(), field: 'menuGroup-2.selectionBCount' }
            ]
          },

          { field: 'canceledCount', headerName: i18n.t('reservationPage.stornoReservationLabel').toString() },
        ],

        onFirstDataRendered (params: any) {
          params.api.sizeColumnsToFit()
        }
      },

      rowData: [],

      detailCellRendererParams: {
        detailGridOptions: {
          columnDefs: [
            { field: 'boarderName', headerName: i18n.t('reservationPage.boarderLabel').toString() },
            { field: 'selectionGroup1', headerName: i18n.t('reservationPage.soupLabel').toString() },
            { field: 'selectionGroup2', headerName: i18n.t('reservationPage.mainDishLabel').toString() },
          ],

          defaultColDef: {
            editable: false,
            resizable: false,

            cellStyle (params: any) {
              return {
                textDecoration: params.data.isCanceled ? 'line-through' : '',
                color: params.data.isCanceled ? 'white' : '',
              }
            }
          },

          getRowStyle (params: any) {
            return {
              backgroundColor: params.data.isCanceled ? 'lightcoral' : '',
              textDecoration: 'line-through'
            }
          },

          onFirstDataRendered (params: any) {
            params.api.sizeColumnsToFit()
          }
        },

        getDetailRowData (params: any) {
          params.successCallback(params.data.boarderList)
        }
      },

      summaryData: [],

      datePeriod: DatePeriod.createFromTomorrow(DatePeriodEnum.Day),

      customerToDownload: undefined,
      dataCancelSource: undefined
    }
  },

  computed: {
    loading (): boolean {
      return this.$store.getters[LOADING_RESERVATION]
    },

    reservationData (): DailyReservation {
      return this.$store.getters[CURRENT_RESERVATION]
    },

    relativeDateIndicator (): string {
      const today = moment(new Date())
      const date = moment(this.datePeriod.startDate)

      let cls: string = ''

      if (date.isBefore(today, 'day')) {
        cls = 'past-date'
      } else if (today.add(1, 'd').isSame(date, 'day')) {
        cls = 'tomorrow-date'
      } else if (date.isAfter(today, 'day')) {
        cls = 'future-date'
      }

      return cls
    }
  },

  methods: {
    onGridReady (): void {
      this.rowData = []
    },

    async fetchData (): Promise<void> {
      try {
        if (this.dataCancelSource) {
          this.dataCancelSource.cancel()
        }

        this.isLoading = true

        this.dataCancelSource = axios.CancelToken.source()

        const data = await this.$store.dispatch(REPORTING_FETCH_RESERVATION_BY_DAY, {
          date: this.datePeriod.formattedStartDate('YYYY-MM-DD'),
          cancelToken: this.dataCancelSource.token
        })

        this.rowData = reservationService.createDataForGrid(data)
        this.summaryData = reservationService.createSummaryDataForGrid(data)

        this.isLoading = false
        this.dataCancelSource = undefined
      } catch (err) {
        // empty, cancel sa nespracovava
      }
    },

    onPeriodSelected (value: DatePeriod): void {
      this.datePeriod = value
      this.fetchData()
    }
  },

  mounted (): void {
    this.fetchData()
  }
})
</script>

<style lang="stylus" scoped>
    .title
        height

    .table_reservation
        border: 4px solid #c0c0c0;
        border-collapse: collapse;
        width 100%
        text-align: center;
        margin-top 20px

    .past-date
        background-color #ffe0b3

    .tomorrow-date
        background-color #baffcd

    .future-date
        background-color #cdf2ff

</style>
